'use client'

import { Button, ButtonProps, styled } from '@mui/material'
import { FC } from 'react'

const CustomButton = styled(Button)(
  ({
    size,
    variant,
    dewiColor,
  }: {
    size: string
    variant: string
    dewiColor: string
  }) => ({
    textTransform: 'none',
    fontSize: {
      small: '0.875rem',
      medium: '1rem',
      large: '1.125rem',
      'extra-large': '1.25rem',
    }[size || 'medium'],
    padding: {
      small: '0 0.75rem',
      medium: '0 1rem',
      large: '0 1.375rem',
      extraLarge: '0 1.375rem',
    }[size || 'medium'],
    color: 'var(--dewi_colors_dark_9) !important',
    minWidth: 'unset',
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    background: 'var(--dewi_colors_gray_1) !important',
    border: '1px solid var(--dewi_colors_gray_4)',
    borderRadius: '62rem',
    '&:disabled': {
      opacity: 0.6,
    },
    '&:hover': {
      backgroundColor: 'var(--dewi_colors_gray_2) !important',
      borderColor: 'var(--dewi_colors_gray_5)',
      color: 'var(--dewi_colors_gray_9)',
    },
    '&:active': {
      backgroundColor: 'var(--dewi_colors_gray_4) !important',
      color: 'var(--dewi_colors_dark_9) !important',
    },
    '&:focus': {
      color: 'var(--dewi_colors_gray_9) !important',
      outline: '4px solid var(--dewi_colors_blue_7)',
    },
    [variant === 'outlined' && '&']: {
      backgroundColor:
        dewiColor === 'error'
          ? 'var(--dewi_colors_red_7) !important'
          : 'var(--dewi_colors_dark_6) !important',
      color:
        dewiColor === 'error'
          ? 'var(--dewi_colors_red_2) !important'
          : 'var(--dewi_colors_gray_3) !important',
      border:
        dewiColor === 'error'
          ? '1px solid var(--dewi_colors_red_5)'
          : '1px solid var(--dewi_colors_dark_4)',

      '&:hover': {
        backgroundColor:
          dewiColor === 'error'
            ? 'var(--dewi_colors_red_7) !important'
            : 'var(--dewi_colors_dark_7) !important',
        borderColor:
          dewiColor === 'error'
            ? 'var(--dewi_colors_red_5)'
            : 'var(--dewi_colors_dark_5)',
        color:
          dewiColor === 'error'
            ? 'var(--dewi_colors_red_1)'
            : 'var(--dewi_colors_gray_9)',
      },
      '&:active': {
        backgroundColor:
          dewiColor === 'error'
            ? 'var(--dewi_colors_red_7) !important'
            : 'var(--dewi_colors_dark_4) !important',
      },
      '&:focus': {
        color:
          dewiColor === 'error'
            ? 'var(--dewi_colors_red_1)'
            : 'var(--dewi_colors_gray_3) !important',
        outline:
          dewiColor === 'error'
            ? '4px solid var(--dewi_colors_red_7)'
            : '4px solid var(--dewi_colors_blue_7)',
      },
    },
    [variant === 'text' && '&']: {
      backgroundColor: 'transparent !important',
      color: 'white !important',
      border: 'none !important',

      '&:hover': {
        color: 'var(--dewi_colors_gray_6) !important',
        backgroundColor: 'transparent !important',
        border: 'none !important',
      },
      '&:active': {
        color: 'white !important',
        backgroundColor: 'transparent !important',
        border: 'none !important',
        outline: '0px solid transparent !important',
      },
      '&:focus': {
        color: 'white !important',
        backgroundColor: 'transparent !important',
        border: 'none !important',
        outline: '0px solid transparent !important',
      },
    },
  }),
)

interface DewiButtonProps extends Omit<ButtonProps, 'size'> {
  size?: 'small' | 'medium' | 'large' | 'extraLarge'
  disabled?: boolean
  dewiColor?: 'primary' | 'secondary' | 'error'
}

const DewiButton: FC<DewiButtonProps> & ButtonProps = ({
  size = 'medium',
  disabled = false,
  children,
  variant,
  color,
  dewiColor,
  ...rest
}) => {
  return (
    <CustomButton
      variant={variant}
      size={size as any}
      disabled={disabled}
      {...rest}
      dewiColor={dewiColor}
      disableRipple
    >
      {children}
    </CustomButton>
  )
}

export default DewiButton
